import { buyColor } from "../../assets/colors";
import { useContext, useMemo } from "react";
import tinycolor from 'tinycolor2';
import {
    Box,
    Typography,
    Checkbox,
    FormGroup,
    FormControlLabel,
} from "@mui/material";
import { useSelector} from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { BarSeriesOptions } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import CommonChartSettingsTabs from "./commonChartSettingsTabs";
import {
    chartSettingsFormGroupSx,
    chartSettingsColorRowLabelSx,
    chartSettingsGridRowSx,
    chartSettingsRowControlsSx,
    chartSettingsSectionTitleSx,
    chartSettingsCheckboxLabelSx,
} from "./chartSettingsLayout";

type Props = {
    viewKey: string;
};

const BarSettingsDialogs = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])

    const chartConfig = useSelector(selectChartConfig(viewKey))
    
    const upColor = useMemo(() => {
        let color = buyColor
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.upColor ?? buyColor
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const downColor = useMemo(() => {
        let color = '#ef5350'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.downColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const thinBars = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.thinBars ?? false
        }
        return false
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: keyof BarSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: newColor
            } as Partial<BarSeriesOptions>);
        }
    };
    
    const handleBooleanChange = (value: boolean) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                thinBars: value
            } as Partial<BarSeriesOptions>);
        }
    };

    const symbolTabContent = (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                BARS
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Up Color</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <ColorSelector compact showOnlySwatch color={upColor.toHexString()} opacity={upColor.getAlpha()}
                            onColorChange={(color, transparency) => {
                                const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                                handleColorChangeSeries(newColor, 'upColor');
                            }} />
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Down Color</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <ColorSelector compact showOnlySwatch color={downColor.toHexString()} opacity={downColor.getAlpha()}
                            onColorChange={(color, transparency) => {
                                const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                                handleColorChangeSeries(newColor, 'downColor');
                            }} />
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <FormControlLabel
                        sx={chartSettingsCheckboxLabelSx}
                        control={
                            <Checkbox
                                checked={thinBars}
                                onChange={(e) => handleBooleanChange(e.target.checked)}
                            />
                        }
                        label="Thin Bar"
                    />
                </Box>
            </FormGroup>
        </Box>
    );

    return (
        <CommonChartSettingsTabs
            viewKey={viewKey}
            symbolTabContent={symbolTabContent}
        />
    );
};

export default BarSettingsDialogs;
